import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { Component, HostBinding, Inject, Injector, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UserWithLeaveInputComponent } from 'src/app/components/field-input/fields/user-with-leave-input/user-with-leave-input.component';
import { FieldType } from '../../services/entities/entity-type.class';
import { EntityFormField, PlanningFormData } from '../../services/entity-type.service';
import { BaseDateTimeInputComponent } from './fields/base-date-time-input/base-date-time-input.component';
import { CheckboxInputComponent } from './fields/checkbox-input/checkbox-input.component';
import { CustomerInputComponent } from './fields/customer-input/customer-input.component';
import { DateInputComponent } from './fields/date-input/date-input.component';
import { DateTimeInputComponent } from './fields/date-time-input/date-time-input.component';
import { DIALOG_CLOSE } from './fields/dialogCloseToken';
import { EntityInputComponent } from './fields/entity-input/entity-input.component';
import { HiringInputComponent } from './fields/hiring-input/hiring-input.component';
import { InputComponent } from './fields/input/input.component';
import { LabelComponent } from './fields/label/label.component';
import { LocationInputComponent } from './fields/location-input/location-input.component';
import { NumberInputComponent } from './fields/number-input/number-input.component';
import { PARTIAL_DATA } from './fields/partialInjectorToken';
import { PersonInputComponent } from './fields/person-input/person-input.component';
import { ProgressDatesComponent } from './fields/progress-dates/progress-dates.component';
import { ProjectInputComponent } from './fields/project-input/project-input.component';
import { RadioInputComponent } from './fields/radio-input/radio-input.component';
import { SelectInputComponent } from './fields/select-input/select-input.component';
import { StatusInputComponent } from './fields/status-input/status-input.component';
import { TextInputComponent } from './fields/text-input/text-input.component';
import { TextareaInputComponent } from './fields/textarea-input/textarea-input.component';
import { TimeInputComponent } from './fields/time-input/time-input.component';
import { UserInputComponent } from './fields/user-input/user-input.component';

const fieldTypeMapping = new Map<FieldType, any>([
    [FieldType.ProgressDates, ProgressDatesComponent],
    [FieldType.Label, LabelComponent],
    [FieldType.Text, TextInputComponent],
    [FieldType.Number, NumberInputComponent],
    [FieldType.Select, SelectInputComponent],
    [FieldType.Date, DateInputComponent],
    [FieldType.DateTime, DateTimeInputComponent],
    [FieldType.Checkbox, CheckboxInputComponent],
    [FieldType.Textarea, TextareaInputComponent],
    [FieldType.Time, TimeInputComponent],
    [FieldType.Radio, RadioInputComponent],
    [FieldType.UserId, UserInputComponent],
    [FieldType.UserIdWithLeaves, UserWithLeaveInputComponent],
    [FieldType.EntityId, EntityInputComponent],
    [FieldType.StatusKey, StatusInputComponent],
    [FieldType.ProjectId, ProjectInputComponent],
    [FieldType.Location, LocationInputComponent],
    [FieldType.BaseDateTime, BaseDateTimeInputComponent],
    [FieldType.Hiring, HiringInputComponent],
    [FieldType.CustomerId, CustomerInputComponent],
    [FieldType.PersonId, PersonInputComponent],
]);

@Component({
    selector: 'app-field-input',
    standalone: false,
    templateUrl: './field-input.component.html',
    styleUrl: './field-input.component.scss',
})
export class FieldInputComponent implements OnInit, OnDestroy {
    @Input() tableStyle = false;
    @Input() field!: EntityFormField;
    @Input() formData?: PlanningFormData;
    @Input() key!: string;
    @Input() hideMatHint = false;

    @HostBinding('class') get hostClasses(): string {
        return `field-${this.key} ` + (this.field.classes ?? '');
    }

    @ViewChild(CdkPortalOutlet) portalOutlet!: CdkPortalOutlet;

    partialPortal: ComponentPortal<InputComponent> | null = null;

    inputTypeMap = fieldTypeMapping;

    private subscription = new Subscription();

    constructor(
        private injector: Injector,
        @Optional() @Inject(DIALOG_CLOSE) private dialogClose$: Observable<any>
    ) {}

    ngOnInit(): void {
        if (this.inputTypeMap.has(this.field.type)) {
            this.partialPortal = new ComponentPortal(
                this.inputTypeMap.get(this.field.type),
                null,
                Injector.create({
                    parent: this.injector,
                    providers: [
                        {
                            provide: PARTIAL_DATA,
                            useValue: {
                                field: this.field,
                                formData: this.formData,
                                tableStyle: this.tableStyle,
                                hideMatHint: this.hideMatHint,
                            },
                        },
                    ],
                })
            );
        }

        if (this.dialogClose$) {
            this.subscription.add(
                this.dialogClose$.subscribe(() => {
                    this.detachPortal();
                })
            );
        }
    }

    private detachPortal(): void {
        this.portalOutlet?.detach();
        this.partialPortal = null;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.detachPortal();
    }
}
